import { t } from "../contract/strings";

/**
 * What a meeting is called on screen (#708): its name, or -- while it has none -- "Meeting"
 * and when it started, in this browser's timezone. A meeting starts unnamed and keeps that
 * fallback until its summary or its creator names it. One rule for every place a title
 * shows (home, history, the meeting screen), and the rule the apps copy:
 *
 *   `Meeting {when}`, where when = day, short English month, year, then the time in the
 *   locale's 12/24-hour cycle: "Meeting 7 Oct 2026, 10:30 AM" / "Meeting 7 Oct 2026, 10:30".
 *
 * The date's order and month names are fixed rather than the locale's, so every client
 * reads the same; only the hour cycle is the device's own setting.
 */

/** Pinned in tests; a caller passes nothing and gets this browser's own. */
export interface WhenOptions {
  timeZone?: string;
  /** The locale whose hour cycle the time follows. */
  locale?: string;
}

/** `true` when the locale reads time on a 12-hour clock. */
function twelveHour(locale: string | undefined): boolean {
  const cycle = new Intl.DateTimeFormat(locale, { hour: "numeric" }).resolvedOptions().hourCycle;
  return cycle === "h12" || cycle === "h11";
}

/**
 * "7 Oct 2026, 10:30 AM": when a meeting started, or `null` for a timestamp that does not
 * parse (a fallback must never read "Invalid Date").
 */
export function fmtMeetingWhen(iso: string | null | undefined, options: WhenOptions = {}): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  const { timeZone, locale } = options;
  const parts = new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone,
  }).formatToParts(date);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? "";
  const time = new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hourCycle: twelveHour(locale) ? "h12" : "h23",
    timeZone,
  })
    .format(date)
    // Newer ICU puts a narrow no-break space before AM/PM; every client writes a plain
    // one. `\s` matches every Unicode space, that one included.
    .replace(/\s/g, " ");
  return `${part("day")} ${part("month")} ${part("year")}, ${time}`;
}

/** The untitled fallback alone, "Meeting 7 Oct 2026, 10:30 AM": the rename field's placeholder. */
export function untitledMeeting(createdAt: string | null | undefined, options: WhenOptions = {}): string {
  const when = fmtMeetingWhen(createdAt, options);
  return when ? t("common.untitled_meeting", { when }) : t("past_session.untitled");
}

/** The meeting's name, or the untitled fallback while it has none. */
export function meetingTitle(
  title: string | null | undefined,
  createdAt: string | null | undefined,
  options: WhenOptions = {}
): string {
  const name = title?.trim();
  return name ? name : untitledMeeting(createdAt, options);
}

/**
 * The history card's stamp, Gmail's rule: a time today, a date this year, a date
 * and year before that.
 *
 * Today is the **viewer's** local day, like every other timestamp in the app, and
 * the comparison is made in the same zone the time is rendered in, so a stamp can
 * never say "today" and then print yesterday's date. Month names are pinned to
 * English and the day is zero-padded, the same way `fmtMeetingWhen` pins its own,
 * so every client reads alike; only the hour cycle follows the device. The year
 * appears only once it is no longer this year, which is the one case where "19
 * Nov" would mislead.
 *
 * `now` and `timeZone` exist for tests (and for the one caller that renders a
 * fixed page); production passes neither. Seconds are instrument noise at this
 * distance and are never shown.
 */
export interface StampOptions {
  /** The moment "today" is judged against. Defaults to this browser's clock. */
  now?: Date;
  timeZone?: string;
  /** The locale whose hour cycle the time follows; defaults to the device's. */
  locale?: string;
  /** Force the cycle rather than reading it from the locale. */
  hour12?: boolean;
}

export function fmtHistoryStamp(
  iso: string | null | undefined,
  options: StampOptions = {}
): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const { now = new Date(), timeZone, locale, hour12 } = options;

  const fields = (value: Date) => {
    const parts = new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      timeZone,
    }).formatToParts(value);
    const part = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((p) => p.type === type)?.value ?? "";
    return { year: part("year"), month: part("month"), day: part("day") };
  };

  const then = fields(date);
  const today = fields(now);

  if (then.year === today.year && then.month === today.month && then.day === today.day) {
    return new Intl.DateTimeFormat(locale, {
      hour: "numeric",
      minute: "2-digit",
      hourCycle: (hour12 ?? twelveHour(locale)) ? "h12" : "h23",
      timeZone,
    })
      .format(date)
      // Newer ICU puts a narrow no-break space before AM/PM; every client writes
      // a plain one. `\s` matches every Unicode space, that one included.
      .replace(/\s/g, " ");
  }

  const day = String(then.day).padStart(2, "0");
  // The month from ICU in English, the way the day and year above come from it: no list
  // of names to drift, and every client pins the same locale.
  const month = new Intl.DateTimeFormat("en-US", { month: "short", timeZone }).format(date);
  return then.year === today.year ? `${day} ${month}` : `${day} ${month} ${then.year}`;
}
